import addDays from "date-fns/addDays";
import formatISO from "date-fns/formatISO";
import startOfToday from "date-fns/startOfToday";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Paper from "@material-ui/core/Paper";

import ErrorBoundary from "components/ErrorBoundary";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { SortOrder } from "interfaces/SortOrder";
import { setSelectedFacility as setWorkoutsPageStoreSelectedFacility } from "store/actions/workoutsPage";
import {
  currentTimeZoneString,
  getNextDates,
  mergeDates
} from "utils/date-utils";
import {
  getCalendarDay,
  getFacilityBlackoutsCalendar
} from "utils/facilityBlackouts";
import { isWorkoutRequest } from "utils/workoutRequest";
import ScheduleDay from "./ScheduleDay";
import ScheduleHeader from "./ScheduleHeader";
import { WORKOUT_REQUEST_GET_OPTIONS } from "./options";

import useStyles from "./styles";

const PAGE_SIZE = 37;
const DAYS_PER_SCREEN = 35;

export interface Props {
  horse: racehorse360.IHorse;
  onChange?: (horse: racehorse360.IHorse) => void;
  onShowMessage?: () => void;
}

const HorseWorkoutSchedule = (props: Props) => {
  const { horse, onShowMessage } = props;
  const classes = useStyles();

  const dispatch = useDispatch();

  const selectedFacility = useAppSelector(
    state => state.workoutsPage.selectedFacility
  );

  const startDate: Date = startOfToday();
  const endDate: Date = addDays(startDate, DAYS_PER_SCREEN - 1);

  const { useListWorkoutRequests, usePullFacilityBlackouts } =
    useRacehorse360Api();

  const listBlackoutDatesKey = `blackout-${selectedFacility?.id}`;
  const { isLoading: isFacilityBlackoutsLoading, data: facilityBlackoutsData } =
    usePullFacilityBlackouts(
      listBlackoutDatesKey,
      {
        facilityId: selectedFacility?.id,
        startDate: formatISO(startDate, {
          representation: "date"
        }),
        endDate: formatISO(endDate, {
          representation: "date"
        })
      },
      {
        enabled: Boolean(selectedFacility),
        initialData: { facilityBlackouts: [] }
      }
    );

  const calendar = getNextDates(startDate, DAYS_PER_SCREEN).map(getCalendarDay);
  const facilityBlackoutsCalendar = getFacilityBlackoutsCalendar(
    facilityBlackoutsData.facilityBlackouts,
    calendar
  );

  const {
    isLoading: isListWorkoutRequestsLoading,
    data: listWorkoutRequestsData,
    refetch: listWorkoutRequestsRefetch
  } = useListWorkoutRequests(
    {
      query: {
        horseIds: [horse?.id],
        date: {
          range: {
            relative: {
              // starting two days ago
              startDate: { value: -2 },
              timezone: currentTimeZoneString()
            }
          }
        },
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      pagingOptions: {
        maxResults: PAGE_SIZE
      },
      getOptions: {
        select: WORKOUT_REQUEST_GET_OPTIONS,
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      enabled: Boolean(horse?.id),
      initialData: { workoutRequests: [] }
    }
  );

  const { workoutRequests } = listWorkoutRequestsData;

  const nextDates = getNextDates(startDate, DAYS_PER_SCREEN).map(date => ({
    date
  }));
  const schedule = workoutRequests.length
    ? mergeDates(nextDates, workoutRequests)
    : nextDates;

  const isLoading = [
    isListWorkoutRequestsLoading,
    isFacilityBlackoutsLoading
  ].some(Boolean);

  const handleChangeFacility = useCallback(
    (facility: racehorse360.IFacility) => {
      dispatch(setWorkoutsPageStoreSelectedFacility(facility));
    },
    [dispatch]
  );

  const handleWorkoutRequestUpdateSuccess = useCallback(async () => {
    onShowMessage();
    listWorkoutRequestsRefetch();
  }, [onShowMessage]);

  return (
    <Paper className={classes.root}>
      <ErrorBoundary>
        <ScheduleHeader
          horse={horse}
          start={startDate}
          end={endDate}
          selectedFacility={selectedFacility}
          onChangeFacility={handleChangeFacility}
        />
      </ErrorBoundary>
      {isLoading ? (
        <Loader />
      ) : (
        <ErrorBoundary>
          <div className={classes.datesList}>
            {schedule.map(day => {
              return (
                <ScheduleDay
                  key={day.date}
                  dayDate={day.date}
                  workoutRequest={isWorkoutRequest(day) ? day : null}
                  horse={horse}
                  facilityBlackoutsCalendar={facilityBlackoutsCalendar}
                  className={classes.date}
                  onUpdate={handleWorkoutRequestUpdateSuccess}
                />
              );
            })}
          </div>
        </ErrorBoundary>
      )}
    </Paper>
  );
};

export default React.memo(HorseWorkoutSchedule);
